/*
	As a general rule, this file should not become a hundreds-of-lines-long-list-of-init-calls.
	It should import whatever is necessary to stand up and render the app, but init-ing of the
	various modules/dependencies should be abstracted out to other modules to keep this one clean.
*/
import config from "./config";
import storage from "@teaming/common-storage";
import { debounce } from "@teaming/common-util";
import { appleSlice } from "@teaming/slice-apple";
import { initFoundation, foundation } from "@teaming/slice-engine";

import routerFactory from "./router";
import { initializeVue } from "./vue-init";
import initVuexHostStores from "./store/vuex-init";
import { getAuthServiceInstance } from "./auth/index";
import telemetryFactory, { wireUpTelemetryToEvents } from "./telemetry";
import initAppDialogs from "./helpers/dialogs";
import initSidebarUtils from "./helpers/sidebarUtils";
import layouts from "./layouts";

// uncomment to see all postal msgs logged:
// import postal from "postal";
// postal.addWireTap( ( data, env ) => {
// 	console.log( env ); // eslint-disable-line
// } );

// AUTHORIZATION
const authService = getAuthServiceInstance();

// TELEMETRY
const telemetry = telemetryFactory( { config, } );

// Vue
// We are primarily standing up component-related assets and an empty Vuex store.
// We init our host stores once we've init'd foundation, so that our usage of
// stores and foundation in the host app matches what we do in the slices.
const { app, i18n, store, } = initializeVue( { config, authService, } );

// ROUTER
const router = routerFactory( {
	app,
	authService,
	telemetry,
	config,
	store,
	layouts,
	i18n,
} );

// CONFIRMATION DIALOGS
const dialogs = initAppDialogs( { store, } );
const sidebar = initSidebarUtils( { store, } );

// FOUNDATION
// these items (along with more defined in @teaming/slice-engine) will be available
// via the foundation() method which is imported from @teaming/slice-engine.
initFoundation( {
	app,
	authService,
	config,
	dialogs,
	sidebar,
	i18n,
	layouts,
	router,
	store,
	telemetry,
} );

// Init host app Vuex stores
initVuexHostStores( { teamlessInboxFeatureFlag: config.teamlessInboxFeatureFlag, } );

// telemetry needs to listen to the auth service events
wireUpTelemetryToEvents();

/*
	If we didn't care about bundle size, we could init all the slices here and they'd all
	get bundled up into the main webpack bundle. However, we're trying to allow our bundles
	to be split up by route (for now - we'll likely tweak this to at least 2 split bundles),
	and so we only init appleSlice here, and we'll let the router init the rest on demand.
*/
// apple slice is the main App infrastructure (minus the App.vue host component)
appleSlice();

// we have our app ready, let's init routing and render it
app.use( router )
	.mount( "#app" );

// NOTE: storage events only fire when the event originates from another
// tab, iframe, etc.
storage.onCrystalAssessment( async () => {
	// both one-on-one & userSettings slice use the Crystal assessments
	// workflow, so we need this setup independently of the slices
	try {
		const assessment = storage.getCrystalAssessment();
		if ( assessment ) {
			await foundation().ioUser.saveAssessment( { assessment, } );
			storage.removeCrystalAssessment();
			// Future iterations will introduce websockets and make this call unnecessary
			// But for now.... HTTP roundtrips are on the menu
			await foundation().ioUser.getUserProfile( { refetch: true, } );
		}
	} catch ( error ) {
		// intentionally just a console statement for now
		// this will change once I add toast-style notifications
		// eslint-disable-next-line no-console
		console.error( "Error saving DISC assessment results", error );
	}
} );

// if the auth0 token is refreshed from another tab, either reload the
// window on this tab, or if this tab is on the signin/singup pages,
// go to the application root
const debounceReload = debounce( () => {
	const { pathname, } = window.location;
	const ignorePaths = [ "/signin", "/signup", "/verify-email", ];
	if ( ignorePaths.find( ignorePath => pathname.endsWith( ignorePath ) ) ) {
		return;
	}
	window.location.reload();
// eslint-disable-next-line no-magic-numbers
}, 500 );

storage.onAuth0TokenChange( () => {
	debounceReload();
} );

// For debugging purposes only. Remove for prod or put behind build flag
if ( config.createTeamingGlobalVariable ) {
	window.__TEAMING__ = {
		...( window.__TEAMING__ || {} ),
		app,
		store,
		router,
		i18n,
		authService: getAuthServiceInstance(),
		telemetry,
		foundation: foundation(),
	};
}
